import React, { useRef } from 'react';
import QRCode from 'react-qr-code';
import { Download, QrCode } from 'lucide-react';
import { getWarrantyStatus, WARRANTY_PERIODS } from '../utils/warranty';
import WarrantyBadge from './WarrantyBadge';

/**
 * AssetQR — generates a QR code with full asset details.
 * IT staff can download. Others can view only.
 */
export default function AssetQR({ asset, computerDetail, canDownload, onClose }) {
  const qrRef = useRef(null);

  // QR content — always use production domain so phone cameras can open it from anywhere
  const qrContent = `https://synergy-node.com/api/assets/public/${asset.asset_number || ''}`;

  const handleDownload = () => {
    // Convert SVG to canvas, then download as PNG
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;
    const canvas = document.createElement('canvas');
    const size = 400;
    canvas.width = size + 40; canvas.height = size + 120;
    const ctx = canvas.getContext('2d');

    // White background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Header text
    ctx.fillStyle = '#1e3a5f';
    ctx.font = 'bold 14px Segoe UI, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('SynergyNode — CL Synergy Group', canvas.width / 2, 25);
    ctx.fillStyle = '#0d1528';
    ctx.font = 'bold 16px Segoe UI, sans-serif';
    ctx.fillText(asset.asset_number, canvas.width / 2, 48);
    ctx.fillStyle = '#475569';
    ctx.font = '12px Segoe UI, sans-serif';
    ctx.fillText(`${asset.brand || ''} ${asset.model || ''}`.trim(), canvas.width / 2, 65);

    // QR code as image
    const svgData = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, 20, 75, size, size);
      ctx.fillStyle = '#475569';
      ctx.font = '10px Segoe UI, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`Holder: ${asset.currentHolder?.full_name || 'Unassigned'}  |  S/N: ${asset.serial_number || '—'}`, canvas.width / 2, size + 95);
      URL.revokeObjectURL(url);
      // Download
      const link = document.createElement('a');
      link.download = `QR_${asset.asset_number}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    img.src = url;
  };

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Asset summary */}
      <div className="w-full rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
          {[
            ['Asset Number', asset.asset_number],
            ['User Name', asset.currentHolder?.full_name || 'Unassigned'],
            ['Brand / Model', `${asset.brand || ''} ${asset.model || ''}`.trim() || '—'],
            ['Serial No', asset.serial_number || '—'],
            ['Purchased Date', asset.purchased_date || '—'],
            ['Hand Over Date', asset.assignments?.[0]?.assigned_date || '—'],
            ...(computerDetail ? [
              ['Windows Key', computerDetail.windows_license_key || '—'],
              ['Office Key', computerDetail.office_license_key || '—'],
            ] : []),
          ].map(([k, v]) => (
            <div key={k} className="flex gap-2">
              <span style={{ color: '#475569', minWidth: 100 }}>{k}:</span>
              <span className="font-medium font-mono text-xs truncate" style={{ color: '#e2e8f0' }}>{v}</span>
            </div>
          ))}
          {/* Warranty with live calculation */}
          <div className="flex gap-2 col-span-2">
            <span style={{ color: '#475569', minWidth: 100 }}>Warranty:</span>
            <WarrantyBadge warrantyPeriod={asset.warranty_period} purchasedDate={asset.purchased_date} small/>
          </div>
        </div>
      </div>

      {/* QR Code */}
      <div ref={qrRef} className="p-4 rounded-xl" style={{ background: '#fff' }}>
        <QRCode value={qrContent} size={200} level="M"/>
      </div>

      <p className="text-xs text-center" style={{ color: '#334155' }}>
        Scan to view asset details<br/>
        <a href={qrContent} target="_blank" rel="noreferrer" className="underline" style={{color:'#60a5fa',fontSize:10}}>
          {qrContent}
        </a>
      </p>

      {/* Download button — IT staff only */}
      {canDownload ? (
        <button onClick={handleDownload} className="btn-primary flex items-center gap-2">
          <Download size={14}/> Download QR Code (PNG)
        </button>
      ) : (
        <div className="text-xs px-3 py-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)', color: '#334155' }}>
          Only IT staff can download QR codes
        </div>
      )}
    </div>
  );
}
